import { computed, defineComponent, ref, nextTick } from '@vue/composition-api';
import { Message } from 'byted-vui';
import Cookies from 'js-cookie';
import { useStore, apis, usePdf2Img } from '@tt4b/ttam-hooks';
import ImageUploadPreview from '@/components/common/image-upload-preview/index.vue';
import { i18n } from '@tt4b/ttam-i18n';
import { trackEvent } from './../../../../utils/index';

const name = 'ttam-audit-reject-appeal-dialog';

export default defineComponent({
  name,
  components: {
    ImageUploadPreview,
  },
  props: {
    showAppealDialog: {
      type: Boolean,
      default: false,
    },
    adgroupId: {
      type: String,
      default: '',
    },
    creativeId: {
      type: String,
      default: '',
    },
  },
  setup(props, ctx) {
    const { state } = useStore();
    const aadvid = computed(() => state.baseInfo.value?.aadvid);

    // 表单数据
    const appealFormData = ref<{ appealReason: string; attachmentList: string[] }>({
      appealReason: '',
      attachmentList: [],
    });
    const appealReasonsLengthHint = computed(() => `${appealFormData.value.appealReason.length}/100`);

    const validateReasons = (rule, value, callback) => {
      if (!value) {
        return callback(new Error(i18n.t('common_appeal_reason_empty_error') as string));
      }
      if (value.length > 100) {
        return callback(new Error(i18n.t('common_appeal_reason_over_max_length_error') as string));
      }

      return callback();
    };

    const appealReasonsRule = computed(() => ({
      appealReason: [{ validator: validateReasons, trigger: 'blur' }],
    }));

    // 图片列表
    const csrfToken = computed(() => Cookies.get('csrftoken'));
    const appealImgsLimit = computed(() => 5);
    const imageUploadUrl = computed(() => `${apis.audit.appealImageUpload}?aadvid=${aadvid.value}`);
    const appealImgList = ref([]);
    const uploadImageList = ref<{ id: number; name: string; status: string }[]>([]);
    const isUploading = ref(false);

    const isUploadedMaxnumImgs = computed(() => appealImgList.value.length >= appealImgsLimit.value);

    // todo 处理pdf包引用导致加载失败的问题
    /** 上传前文件校验 */
    const { pdfToImgFile } = usePdf2Img();
    /** 上传之前的校验 */
    const handleBeforeUpload = async file => {
      if (Math.floor(file.size) >= 1024 * 1024 * 10) {
        Message.error(i18n.t('common_appeal_img_oversize_error') as string);
        return false;
      }
      isUploading.value = true;
      uploadImageList.value.push({
        id: file.uid,
        name: file.name,
        status: 'uploading',
      });

      trackEvent('appeal_image_upload', {
        eventInfo: {
          ad_id: props.adgroupId,
          creative_id: props.creativeId,
          aadvid: aadvid.value,
          isEdit: true,
          file: file.name,
        },
      });

      try {
        pdfToImgFile(file, 10);
      } catch (err) {
        Message.error(err?.message as string);
      }
    };

    const updateFormData = () => {
      const fileList = appealImgList.value;
      appealFormData.value.attachmentList = fileList.reduce((list: string[], image: any) => {
        if (image.status === 'success') {
          const data = image?.response?.data;
          if (data?.url) {
            list.push(data?.url);
          }
        }

        return list;
      }, []);
      uploadImageList.value = fileList.map((item: any) => ({
        id: item.uid,
        name: item.name,
        status: item.status,
      }));
    };

    /** 删除图片，重置数组 */
    const handleRemoveImage = image => {
      appealImgList.value = appealImgList.value.filter((item: any) => item.uid !== image.id);
      nextTick(() => {
        updateFormData();
      });
    };

    /** 图片超过数量 */
    const handleUploadExceed = () => {
      Message.error(i18n.t('common_appeal_img_num_limit_alert') as string);
    };

    /** 上传失败 */
    const handleUploadError = (err, file, fileList: any[] = []) => {
      isUploading.value = false;
      // 阻止upload组件仍然展示失败的文件
      const index = fileList.indexOf(file);
      if (index >= 0) {
        fileList.splice(index, 1);
      }
      uploadImageList.value = uploadImageList.value.filter((item: any) => item.id !== file.uid);
      Message.error(i18n.t('common_failed') as string);
    };

    /** 上传成功 */
    const handleUploadSuccess = (response, file, fileList = []) => {
      const { code } = response || {};
      if (code === 0) {
        appealImgList.value = fileList;
        isUploading.value = false;
        nextTick(() => {
          updateFormData();
        });
      } else {
        handleUploadError('upload error', file, fileList);
      }
    };

    // 整体表单提交
    const refForm = ref();
    const clearAppealReasonsData = () => {
      appealFormData.value = {
        appealReason: '',
        attachmentList: [],
      };

      appealImgList.value = [];
      nextTick(() => {
        updateFormData();
      });

      uploadImageList.value = [];
      isUploading.value = false;

      refForm.value.clearValidate && refForm.value.clearValidate();
      ctx.emit('close');
    };

    const handleAppealReasonsSubmit = () => {
      refForm.value.validate(valid => {
        if (valid && !isUploading.value) {
          ctx.emit('submit', appealFormData.value);
          clearAppealReasonsData();
        } else {
          return false;
        }
      });
    };

    return {
      name,
      refForm,
      // 表单相关
      csrfToken,
      appealImgsLimit,
      imageUploadUrl,
      appealFormData,
      appealReasonsRule,
      appealReasonsLengthHint,
      clearAppealReasonsData,
      handleAppealReasonsSubmit,

      // 图片上传相关
      isUploading,
      appealImgList,
      uploadImageList,
      isUploadedMaxnumImgs,
      handleUploadExceed,
      handleRemoveImage,
      handleUploadError,
      handleUploadSuccess,
      handleBeforeUpload,
    };
  },
});
